import { useState } from 'react';
import {
  Package, AlertTriangle, FileText, DollarSign, TrendingUp, ShoppingCart,
} from 'lucide-react';
import { KPICard } from '@mustauto/ui/web';
import { useInventory } from '@/hooks/useInventory';
import { useInventoryInsights } from '@/hooks/useInventoryInsights';
import { usePurchaseOrders } from '@/hooks/usePurchaseOrders';
import type { InventoryItem } from '@/hooks/useInventory';
import { formatReais, formatNumber } from '@/lib/utils';
import { Breadcrumbs } from '@mustauto/ui/web';
import { ItemsTab } from './inventory/ItemsTab';
import { PurchaseOrdersTab } from './inventory/PurchaseOrdersTab';
import { SuppliersTab } from './inventory/SuppliersTab';
import { MovementsTab } from './inventory/MovementsTab';
import { useCan } from '@/hooks/useCan';

type Tab = 'items' | 'orders' | 'suppliers' | 'movements';

export function InventoryPage() {
  const [activeTab, setActiveTab] = useState<Tab>('items');
  const [poPreselectedItemId, setPOPreselectedItemId] = useState<string | null>(null);

  // KPI data
  const { items, alertCount, isError: itemsError } = useInventory();
  const { insights, isErrorInsights } = useInventoryInsights();
  const { orders: openOrders, isError: openOrdersError } = usePurchaseOrders({ status: 'ordered' });
  const { orders: draftOrders, isError: draftOrdersError } = usePurchaseOrders({ status: 'draft' });

  /**
   * Com a busca falhada a lista chega vazia (os hooks devolvem `?? []`), e um
   * KPI de "R$ 0,00 em estoque" é uma afirmação sobre o patrimônio da oficina
   * que ninguém fez. Travessão diz "não sei"; zero diz "não tem".
   *
   * Cada cartão pergunta pela busca que o alimenta — são três independentes, e
   * guardar só uma delas produz o pior híbrido: "Total de itens —" ao lado de
   * "Itens críticos 0", que o gestor lê como "está tudo certo".
   */
  const kpi = (failed: boolean, fn: () => string) => (failed ? '—' : fn());
  const canSeeCost = useCan()('estoque.custo');

  const totalItems = items.length;
  const totalValue = items.reduce((sum, i) => sum + i.quantity * (i.avgCost ?? i.unitCost ?? 0), 0);
  const openOCCount = openOrders.length + draftOrders.length;

  // Gasto do mês corrente (aproximado pelos pedidos recebidos no mês)
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const { orders: receivedThisMonth, isError: monthSpendError } = usePurchaseOrders({
    status: 'received',
    from: monthStart,
  });
  const monthSpend = receivedThisMonth.reduce((sum, o) => sum + (o.totalAmount ?? 0), 0);
  const ordersError = openOrdersError || draftOrdersError;

  const criticalItems = items.filter((i) => i.quantity === 0).length;
  const highTurnoverItems = insights.filter((i) => i.giroLabel === 'alto').length;

  const handleCreatePOFromItem = (item: InventoryItem) => {
    setPOPreselectedItemId(item.id);
    setActiveTab('orders');
  };

  const tabs: { key: Tab; label: string; badge?: number }[] = [
    { key: 'items', label: 'Itens' },
    { key: 'orders', label: 'Ordens de Compra', badge: openOCCount > 0 ? openOCCount : undefined },
    { key: 'suppliers', label: 'Fornecedores' },
    { key: 'movements', label: 'Movimentações' },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Estoque' },
        ]}
      />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Estoque</h1>
        <p className="text-sm text-muted-foreground">
          Controle de peças e insumos da oficina
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <KPICard
          title="Total de itens"
          value={kpi(itemsError, () => formatNumber(totalItems))}
          icon={<Package size={20} />}
        />
        {/* Valor e gasto saem do custo de compra (`estoque.custo`). */}
        {canSeeCost && (
          <KPICard
            title="Valor em estoque"
            value={kpi(itemsError, () => formatReais(totalValue))}
            icon={<DollarSign size={20} />}
          />
        )}
        <KPICard
          title="OCs abertas"
          value={kpi(ordersError, () => formatNumber(openOCCount))}
          icon={<FileText size={20} />}
          subtitle={openOCCount > 0 ? `${draftOrders.length} rascunhos` : undefined}
        />
        {canSeeCost && (
          <KPICard
            title="Gasto no mês"
            value={kpi(monthSpendError, () => formatReais(monthSpend))}
            icon={<ShoppingCart size={20} />}
          />
        )}
        <KPICard
          title="Itens críticos"
          value={kpi(itemsError, () => formatNumber(criticalItems + alertCount))}
          icon={<AlertTriangle size={20} />}
          subtitle={criticalItems > 0 ? `${criticalItems} zerados` : undefined}
        />
        <KPICard
          title="Giro alto"
          value={kpi(isErrorInsights, () => formatNumber(highTurnoverItems))}
          icon={<TrendingUp size={20} />}
        />
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-1 -mb-px">
          {tabs.map(({ key, label, badge }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === key
                  ? 'border-b-2 border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
              {badge !== undefined && (
                <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                  {badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'items' && <ItemsTab onCreatePO={handleCreatePOFromItem} />}
      {activeTab === 'orders' && (
        <PurchaseOrdersTab
          preselectedItemId={poPreselectedItemId}
          onClearPreselect={() => setPOPreselectedItemId(null)}
          onNavigateToSuppliers={() => setActiveTab('suppliers')}
        />
      )}
      {activeTab === 'suppliers' && <SuppliersTab />}
      {activeTab === 'movements' && <MovementsTab />}
    </div>
  );
}
